import { useState } from "react";
import { useTestimonials, Testimonial } from "@/hooks/useTestimonials";
import { testimonialsApi } from "@/integrations/mysql/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Pencil, Trash2, Plus, Star, GripVertical, Eye, EyeOff } from "lucide-react";
import { PageVisibilityToggle } from "@/components/ui/PageVisibilityToggle";
import { PhotoCropUpload } from "@/components/ui/PhotoCropUpload";
import { TestimonialCard } from "./TestimonialCard";
import { useToast } from "@/hooks/use-toast";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// ─── Sortable row ──────────────────────────────────────────────────────────
function SortableTestimonial({
  testimonial, onEdit, onDelete, onToggle, disabled,
}: {
  testimonial: Testimonial;
  onEdit: (t: Testimonial) => void;
  onDelete: (id: string) => void;
  onToggle: (id: string, current: boolean) => void;
  disabled: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: testimonial.id });

  return (
    <Card
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing mt-1 text-gray-400 hover:text-gray-600 transition-colors shrink-0">
            <GripVertical className="w-5 h-5" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="font-semibold text-sm text-gray-800">{testimonial.author_name}</span>
              {testimonial.author_role && (
                <span className="text-xs text-gray-400">· {testimonial.author_role}</span>
              )}
              <div className="flex ml-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className={`w-3 h-3 ${i < testimonial.rating ? "text-yellow-400 fill-current" : "text-gray-200"}`} />
                ))}
              </div>
            </div>
            <p className="text-xs text-gray-500 line-clamp-2">{testimonial.testimonial_text}</p>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={() => onToggle(testimonial.id, testimonial.is_active)}
              className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                testimonial.is_active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"
              }`}
            >
              {testimonial.is_active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
              {testimonial.is_active ? "Visível" : "Oculto"}
            </button>
            <Button size="sm" variant="outline" onClick={() => onEdit(testimonial)} disabled={disabled} className="h-7 w-7 p-0">
              <Pencil className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" variant="outline" onClick={() => onDelete(testimonial.id)} className="h-7 w-7 p-0 text-red-500 hover:text-red-600 hover:border-red-300">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Main component ────────────────────────────────────────────────────────
export function TestimonialsManager() {
  const { testimonials, isLoading, createTestimonial, updateTestimonial, deleteTestimonial } = useTestimonials(false);
  const [localItems, setLocalItems] = useState<Testimonial[] | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    author_name: "", testimonial_text: "", rating: 5,
    author_role: "", author_photo_url: "", is_active: true, display_order: 0,
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const displayed = localItems ?? testimonials;

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = displayed.findIndex(t => t.id === active.id);
    const newIdx = displayed.findIndex(t => t.id === over.id);
    const reordered = arrayMove(displayed, oldIdx, newIdx);
    setLocalItems(reordered);
    try {
      await Promise.all(reordered.map((t, i) => testimonialsApi.update(t.id, { display_order: i })));
      toast({ title: "Ordem atualizada!" });
    } catch {
      toast({ title: "Erro ao atualizar ordem", variant: "destructive" });
      setLocalItems(null);
    }
  };

  const resetForm = () => setFormData({
    author_name: "", testimonial_text: "", rating: 5,
    author_role: "", author_photo_url: "", is_active: true, display_order: displayed.length,
  });

  const handleEdit = (t: Testimonial) => {
    setEditingId(t.id);
    setFormData({ author_name: t.author_name, testimonial_text: t.testimonial_text, rating: t.rating,
      author_role: t.author_role || "", author_photo_url: t.author_photo_url || "",
      is_active: t.is_active, display_order: t.display_order });
    setIsCreating(false);
  };

  const handleSave = () => {
    if (editingId) {
      updateTestimonial({ id: editingId, updates: formData });
      setEditingId(null);
    } else if (isCreating) {
      createTestimonial(formData);
      setIsCreating(false);
    }
    resetForm();
    setLocalItems(null);
  };

  const handleCancel = () => { setEditingId(null); setIsCreating(false); resetForm(); };
  const isValid = formData.author_name.length >= 2 && formData.testimonial_text.length >= 10 && formData.rating >= 1;

  if (isLoading) return <div className="p-4 text-sm text-gray-400">Carregando depoimentos...</div>;

  return (
    <div className="space-y-4">
      <PageVisibilityToggle pageKey="page_depoimentos_visible" label="Seção de Depoimentos" />

      <div className="flex justify-end">
        <Button onClick={() => { setIsCreating(true); resetForm(); }} disabled={isCreating || !!editingId}>
          <Plus className="w-4 h-4 mr-2" /> Novo Depoimento
        </Button>
      </div>

      {(isCreating || editingId) && (
        <Card className="border-2 border-brand-primary/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{editingId ? "Editar Depoimento" : "Novo Depoimento"}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-gray-600">Nome* ({formData.author_name.length}/100)</Label>
                <Input className="mt-1" value={formData.author_name} onChange={e => setFormData(p => ({ ...p, author_name: e.target.value }))} placeholder="Ex: Maria Santos" maxLength={100} />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-gray-600">Função (opcional)</Label>
                <Input className="mt-1" value={formData.author_role} onChange={e => setFormData(p => ({ ...p, author_role: e.target.value }))} placeholder="Ex: Paciente desde 2023" maxLength={100} />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-gray-600">Depoimento* ({formData.testimonial_text.length}/500)</Label>
              <Textarea className="mt-1" value={formData.testimonial_text} onChange={e => setFormData(p => ({ ...p, testimonial_text: e.target.value }))} rows={4} maxLength={500} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-gray-600">Avaliação</Label>
                <div className="flex gap-1 mt-2">
                  {[1,2,3,4,5].map(s => (
                    <Star key={s} className={`w-6 h-6 cursor-pointer ${s <= formData.rating ? "text-yellow-400 fill-current" : "text-gray-200"}`} onClick={() => setFormData(p => ({ ...p, rating: s }))} />
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-gray-600 block mb-2">Foto (opcional)</Label>
                <PhotoCropUpload
                  value={formData.author_photo_url}
                  onChange={url => setFormData(p => ({ ...p, author_photo_url: url }))}
                  shape="circle"
                  aspect={1}
                  label="Foto do autor"
                  previewClassName="w-14 h-14 rounded-full"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={formData.is_active} onCheckedChange={v => setFormData(p => ({ ...p, is_active: v }))} />
              <Label>Visível no site</Label>
            </div>
            <div className="border-t pt-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Preview</p>
              <div className="max-w-md">
                <TestimonialCard authorName={formData.author_name || "Nome"} testimonialText={formData.testimonial_text || "Texto do depoimento..."} rating={formData.rating} authorRole={formData.author_role} authorPhotoUrl={formData.author_photo_url} />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={!isValid}>Salvar</Button>
              <Button variant="outline" onClick={handleCancel}>Cancelar</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {displayed.length === 0 ? (
        <div className="text-center py-10 text-gray-400 text-sm">Nenhum depoimento cadastrado.</div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-gray-400 flex items-center gap-1.5"><GripVertical className="w-3.5 h-3.5" />Arraste para reordenar</p>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={displayed.map(t => t.id)} strategy={verticalListSortingStrategy}>
              {displayed.map(t => (
                <SortableTestimonial key={t.id} testimonial={t} onEdit={handleEdit} onDelete={id => setDeleteId(id)}
                  onToggle={(id, cur) => updateTestimonial({ id, updates: { is_active: !cur } })} disabled={!!editingId || isCreating} />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir depoimento</AlertDialogTitle>
            <AlertDialogDescription>Tem certeza? Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (deleteId) { deleteTestimonial(deleteId); setDeleteId(null); setLocalItems(null); } }} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
